import { slideUp, slideDown } from '../../js/theme/helpers';
import { Stat } from '../stats/script';

/**
 * Represents a Tabs component.
 */
class Tabs {
	/**
	 * The index of the currently active panel.
	 * @type {number}
	 */
	currentPanelIndex = 0;

	/**
	 * Indicates whether the tabs are displayed vertically.
	 * @type {boolean}
	 */
	isVertical = false;

	/**
	 * Creates an instance of Tabs.
	 * @param {HTMLElement} el - The root element of the Tabs component.
	 * @param {Object} options - Additional options for the Tabs component.
	 */
	constructor(el, options = {}) {
		if (el) {
			this.el = el;
			this.tabsEl = this.el.querySelector('.wp-block-embark-tabs__tablist');
			this.panels = [...this.el.querySelectorAll('.wp-block-embark-tabpanel')];
			this.options = {
				accordionSpeed: 300,
				breakpoint: 'md',
				autoCloseSiblings: false,
				...options,
			};

			if (this.el.classList.contains('is-style-vertical')) {
				this.isVertical = true;
			}

			// Duplicate tab controls within tabpanels and append them to the tablist element.
			if (this.tabsEl && this.panels.length) {
				this.panels.forEach((panel, i) => {
					const tab = panel.querySelector('.wp-block-embark-tabs__control');

					const tabClone = tab.cloneNode(true);
					tabClone.removeAttribute('id');
					this.tabsEl.appendChild(tabClone);

					setTimeout(() => {
						tabClone.classList.add('animate');
					}, (i + 1) * 100);
				});
			}

			// Add click handlers to all tab controls.
			this.tabs = [...this.el.querySelectorAll('.wp-block-embark-tabs__control')];
			this.tabs.forEach((tab) => {
				tab.addEventListener('click', this.handleTabClick);
				tab.addEventListener('keydown', this.handleKeydown);
			});

			window.addEventListener('resize', this.handleResize);

			// Open the first tab.
			if (this.useTabs()) {
				this.open(this.tabs[0], false);
			}
		}
	}

	/**
	 * Retrieves the panel associated with the given tab.
	 * @param {HTMLElement} tab - The tab element.
	 * @returns {HTMLElement|boolean} The panel element or false if not found.
	 */
	getPanelFromTab = (tab) => {
		if (tab) {
			const panelId = tab.getAttribute('aria-controls');
			return document.getElementById(panelId);
		}

		return false;
	};

	/**
	 * Closes the specified tab.
	 * @param {HTMLElement} tab - The tab element to close.
	 */
	close = (tab) => {
		if (tab) {
			const panel = this.getPanelFromTab(tab);

			if (panel) {
				panel.classList.remove('active');
				tab.setAttribute('aria-selected', 'false');
				tab.setAttribute('tabindex', '-1');

				const controls = tab.getAttribute('aria-controls');
				const tabInner = document.querySelector(`#${controls}`).querySelector('.wp-block-embark-tabpanel__inner');
				if (tabInner && this.useAccordions()) {
					slideUp(tabInner, `${this.options.accordionSpeed}ms`);
				}
			}
		}
	};

	/**
	 * Opens the specified tab.
	 * @param {HTMLElement} tab - The tab element to open.
	 * @param {boolean} [setFocus=true] - Whether to set focus to the opened tab.
	 */
	open = (tab, setFocus = true) => {
		if (tab) {
			const panel = this.getPanelFromTab(tab);
			if (panel) {
				// Close the previous tab.
				if (this.useTabs() || (this.useAccordions && this.options.autoCloseSiblings)) {
					const prevTab = this.el.querySelector('.wp-block-embark-tabs__control[aria-selected="true"]');
					this.close(prevTab);
				}

				// Open the new one.
				panel.classList.add('active');
				tab.setAttribute('aria-selected', 'true');
				tab.removeAttribute('tabindex');

				if (setFocus) {
					tab.focus();
				}

				// Update the tab index.
				this.tabs.forEach((_tab, i) => {
					if (tab === _tab) {
						this.currentPanelIndex = i;
					}
				});

				const controls = tab.getAttribute('aria-controls');
				const tabInner = document.querySelector(`#${controls}`).querySelector('.wp-block-embark-tabpanel__inner');
				if (tabInner && this.useAccordions()) {
					slideDown(tabInner, `${this.options.accordionSpeed}ms`);
				}

				if (!panel.classList.contains('stats-init')) {
					const statsBlock = panel.querySelector('.wp-block-embark-stats');
					if (statsBlock) {
						const statDelay = parseInt(statsBlock.style.transitionDelay || 0);
						const stats = [...statsBlock.querySelectorAll('.js-counter[data-number]')];
						if (stats.length) {
							setTimeout(() => {
								stats.map((el) => new Stat(el));
							}, statDelay);
						}
					}

					panel.classList.add('stats-init');
				}
			}
		}
	};

	/**
	 * Opens the next tab.
	 */
	next = () => {
		const panelIndex = this.currentPanelIndex + 1 < this.panels.length ? this.currentPanelIndex + 1 : 0;
		this.open(this.tabs[panelIndex]);
	};

	/**
	 * Opens the previous tab.
	 */
	prev = () => {
		const panelIndex = this.currentPanelIndex - 1 > -1 ? this.currentPanelIndex - 1 : this.panels.length - 1;
		this.open(this.tabs[panelIndex]);
	};

	/**
	 * Event handler for tab click event.
	 * @param {Event} e - The click event object.
	 */
	handleTabClick = (e) => {
		const tab = e.currentTarget;
		if (this.useTabs()) {
			this.open(tab);
		} else if (tab.getAttribute('aria-selected') === 'true') {
			this.close(tab);
		} else {
			this.open(tab);
		}
	};

	/**
	 * Event handler for keydown event.
	 * @param {Event} e - The keydown event object.
	 */
	handleKeydown = (e) => {
		let flag = false;

		switch (e.key) {
			case 'ArrowLeft':
				if (!this.useVerticalNavigation()) {
					this.prev();
					flag = true;
				}
				break;

			case 'ArrowRight':
				if (!this.useVerticalNavigation()) {
					this.next();
					flag = true;
				}
				break;

			case 'ArrowUp':
				if (this.useVerticalNavigation()) {
					this.prev();
					flag = true;
				}
				break;

			case 'ArrowDown':
				if (this.useVerticalNavigation()) {
					this.next();
					flag = true;
				}
				break;

			case 'Home':
				this.open(this.tabs[0]);
				flag = true;
				break;

			case 'End':
				this.open(this.tabs[this.panels.length - 1]);
				flag = true;
				break;

			default:
				break;
		}

		if (flag) {
			e.stopPropagation();
			e.preventDefault();
		}
	};

	/**
	 * Checks if vertical navigation should be used.
	 * @returns {boolean} - Whether to use vertical navigation.
	 */
	useVerticalNavigation = () => this.isVertical && this.useTabs();

	/**
	 * Checks if tabs should be used based on the current breakpoint.
	 * @returns {boolean} - Whether to use tabs.
	 */
	useTabs = () => embark.breakpoints.above(this.options.breakpoint);

	/**
	 * Checks if accordions should be used based on the current breakpoint.
	 * @returns {boolean} - Whether to use accordions.
	 */
	useAccordions = () => embark.breakpoints.below(this.options.breakpoint);

	/**
	 * Event handler for resize event.
	 */
	handleResize = () => {
		window.requestAnimationFrame(() => {
			if (this.useTabs()) {
				this.panels.forEach((panel) => {
					const inner = panel.querySelector('.wp-block-embark-tabpanel__inner');
					if (inner) {
						inner.style = null;
					}
				});
			}
		});
	};
}

document.addEventListener('DOMContentLoaded', () => {
	// Find all tabs blocks and initialize Tabs component for each block.
	const tabsBlocks = [...document.querySelectorAll('.wp-block-embark-tabs')];
	if (tabsBlocks.length) {
		tabsBlocks.map((el) => new Tabs(el));
	}
});
